define("Payment", [`Class`, `module`], function (Super, Self) {
	return Super.inherit({
		namespace: Self.id,
		requires: [`App`, `DOM`, `Locale`, `System`, `User`, `XHR`], // needed for fetching the client token from the server, and talking with braintree
		singleton: true, // utility class, only one instance
		prepare(setup) {
			const { Locale, data, promises } = this;
			promises.stripe = {};

			data.isWallet = false;

			// NOTE: circular dependency risk, do not include in _requires_ array or thread into constructor
			this.require({ deps: [`ShoppingCart`] });

			Locale.get({
				fireImmediately: true,
				subscribe: ({ selected }) => {
					const { stripeKey } = selected;

					data.stripeLocale = stripeKey;
					this.updateLocale();
				},
			});
		},

		api: {
			getPaymentToken: {
				async: true,
				value(options) {
					return this.XHR.ajax({
						// obtain a client token from the server
						url: `/ajax/payment/generateToken`,
						then: (response) => response.token,
					});
				},
			},

			fetchDeviceData: {
				async: true,
				value(options) {
					const { Deferred, promises } = this;
					const deferred = Deferred();
					let { promise } = deferred;

					if (this.isPromise(promises.deviceData)) {
						// already pending or failed
						return promises.deviceData;
					}

					window.onBraintreeDataLoad = () => {
						try {
							// wait for the extra security step to load
							const BraintreeData = window.BraintreeData;
							const merchantId = this.Config.BRAINTREE_MERCHANT_ID;
							const form = document.createElement(`form`);
							const uniqueId = this.uniqueId();
							const formId = `bt-form-${uniqueId}`;
							let deviceData;

							// Create a dummy form for the BT api to attach to
							form.id = formId;
							document.body.appendChild(form);

							// Initialize the BT-data library
							BraintreeData.setup(merchantId, formId, BraintreeData.environments.production);
							// Extract the deviceData string from the input the library creates
							deviceData = form.getElementsByTagName(`input`)[0].value;

							// Clean up the DOM and global scope
							document.body.removeChild(form);
							delete window.onBraintreeDataLoad;
							delete window.BraintreeData;

							deferred.resolve(deviceData);
						} catch (err) {
							deferred.reject();
						}
					};

					this.defer({
						duration: 3000,
						then: deferred.reject,
					});

					require({
						deps: [`braintree_data`],
						done: true,
					});

					promise = this.when({
						promise,
						fail: (reason) => {
							this.log(`Error loading braintree_data dependency, passing null`);
							return null;
						},
					});

					promises.deviceData = promise;
					return promise;
				},
			},

			getDeviceData: {
				public: true,
				value(context) {
					return this.data.deviceData; // string, will return by value
				},
			},

			useBraintree: {
				async: true,
				value: function (options) {
					const { App, promises } = this;
					let promise;

					if (this.isPromise(promises.braintree)) {
						return promises.braintree;
					}

					promise = this.when({
						promises: [
							this.require({
								deps: [`braintree`],
								then: (deps) => deps[0],
							}),
							this.getPaymentToken(),
							this.fetchDeviceData(),
						],
						spread: (braintreeApi, token, deviceData) => {
							const { data } = this;
							data.deviceData = deviceData;

							return [braintreeApi, token];
						},
						fail: (reason) => {
							App.notifyUser({
								type: `error`,
								message: `We're having trouble communicating with the payment gateway, please try again.`,
							});

							promises.braintree = null; // reset the promise, swallow the error
						},
					});

					return (promises.braintree = promise);
				},
			},

			useStripe: {
				async: true,
				public: true,
				value(context, options) {
					const { Config, data, promises } = this;
					const stripePromises = promises.stripe;
					const { stripeAccount } = options;
					const { stripeLocale } = data;
					const useStripeAccount = this.isString(stripeAccount, 1);
					const key = useStripeAccount ? stripeAccount : `none`;
					let promise = stripePromises[key];

					if (this.isUndefined(promise)) {
						const { STRIPE_PUBLISHABLE_KEY } = Config;
						const params = useStripeAccount ? { stripeAccount } : {};

						params.locale = stripeLocale;
						params.betas = [`blocked_card_brands_beta_2`];

						promise = this.require({
							deps: [`stripe`],
							spread: (Stripe) => Stripe(STRIPE_PUBLISHABLE_KEY, params),
						});

						stripePromises[key] = promise;
					}

					return promise;
				},
			},

			updateLocale: {
				value() {
					const { data } = this;
					const { addressElementsGroup, billingElementsGroup, cvcElementsGroup, stripeLocale } = data;

					if (this.isObject(addressElementsGroup)) {
						addressElementsGroup.update({ locale: stripeLocale });
					}

					if (this.isObject(billingElementsGroup)) {
						billingElementsGroup.update({ locale: stripeLocale });
					}

					if (this.isObject(cvcElementsGroup)) {
						cvcElementsGroup.update({ locale: stripeLocale });
					}
				},
			},

			authorizeCard: {
				async: true,
				public: true,
				value(context, options) {
					return this.useBraintree({
						spread: function (braintree, clientToken) {
							const client = new braintree.api.Client({
								clientToken,
							});
							const cvv = options.cvv;
							const number = options.number;
							const deferred = this.Deferred();
							const address = this.isObject(options.billingAddress) ? options.billingAddress : {};
							const expirationDate = options.expDate.replace(`/`, `/20`); // format how BRAINTREE expects, 4 digit date
							const cardOptions = {
								cvv,
								number,
								expirationDate,
								billingAddress: {
									countryCodeAlpha2: address.countryCodeAlpha2,
									streetAddress: address.streetAddress,
									extendedAddress: null,
									locality: address.city,
									region: address.state,
									postalCode: address.postalCode,
								},
							};

							client.tokenizeCard(cardOptions, (err, nonce) => {
								// response received from braintree
								if (this.isString(nonce, 1)) {
									deferred.resolve(nonce);
								} else {
									deferred.reject(err);
								}
							});

							return deferred.promise;
						},
					});
				},
			},

			addMethod: {
				async: true,
				public: true,
				defaults: {
					paymentProcessorType: null,
					orderId: null,
				},
				async value(context, options) {
					const { User, Utils, XHR } = this;
					const { action, expiration_window, orderId, paymentProcessorType } = options;

					if (paymentProcessorType === `STRIPE`) {
						const user = await User.get();
						let redirectUrl = `${window.location.href}?action=${action}&orderId=${orderId}`;

						// Manage Waitlist uses this
						if (expiration_window != null) {
							redirectUrl += `&expiration_window=${expiration_window}`;
						}

						if (this.isArray(user.paymentMethods, 1)) {
							const paymentMethods = this.map(user.paymentMethods, `token`).join(`,`);
							redirectUrl += `&paymentMethods=${paymentMethods}`;
						}

						return this.confirmSetupIntent({ redirectUrl }).then((setupIntent) => {
							// NOTE: method will actually be added to the user object upon being used to update an order
							return {
								// return a bare-bones version of the authorized method
								paymentProcessorType,
								token: setupIntent.payment_method,
							};
						});
					} // else BRAINTREE

					return this.when({
						promises: [
							User.get({
								then: (user) => Utils.map(user.paymentMethods, `token`),
							}),
							this.require({
								deps: [`Captcha`],
								then: () => this.Captcha.getResponseToken(),
							}),
						],
						spread: (existingTokens, recaptchaResponse) => {
							const { nonce, useAsDefault } = options;
							const body = {
								nonce,
								useAsDefault,
								recaptchaResponse,
							};
							let url = `/api/creditcardandbillings`;

							if (this.isString(orderId)) {
								url += `?order_id=${orderId}`;
							}

							if (options.type === `CREDIT_CARD`) {
								body.billingAddress = options.billingAddress;
							}

							return XHR.ajax({
								type: `POST`,
								url,
								REST: true,
								data: body,
							})
								.then((response) =>
									User.set({
										// notify the User singleton of the change
										value: response,
									})
								)
								.then((user) => {
									return this.chain(user.paymentMethods)
										.find((method) => {
											if (!this.includes(existingTokens, method.token)) {
												// didn't exist a moment ago, assume this is the new one
												return true; // match found, break loop and resolve with this method
											}
										})
										.tap((newMethod) => {
											if (!this.isObject(newMethod)) {
												throw new Error(`Payment.addMethod: new payment method not found`);
											}
										})
										.value();
								});
						},
					});
				},
			},

			// used to convert tixr payment methods (platform account) to stripe connect methods (client account-specific)
			translateMethod: {
				async: true,
				public: true,
				value(context, options) {
					const { User, XHR } = this;
					const { groupId, token } = options;
					let { userId } = options;

					if (userId == null) {
						return User.get({
							then: (user) =>
								XHR.ajax({
									type: `GET`,
									url: `/api/users/${user.id}/payment-methods/${token}/groups/${groupId}`,
								}),
						});
					}

					return XHR.ajax({
						type: `GET`,
						url: `/api/users/${userId}/payment-methods/${token}/groups/${groupId}`,
					});
				},
			},

			removeMethod: {
				async: true,
				public: true,
				defaults: {
					token: null,
				},
				value(context, options) {
					if (this.isNull(options.token)) {
						throw new Error(`Payment.removeMethod: options.token is required.`);
					}

					return this.XHR.ajax({
						type: `DELETE`,
						url: `/api/creditcards/${options.token}`,
						then: (response) =>
							this.User.set({
								// notify the User singleton of the change
								value: response,
							}),
					});
				},
			},

			createSetupIntent: {
				async: true,
				public: true,
				value(context, options) {
					this.clearStripeElementsInfo();

					const create = (userId) =>
						this.XHR.ajax({
							data: {
								event_id: options?.eventId,
								shopping_cart_id: options?.shoppingCartId,
								current_payment_token: options?.currentPaymentToken,
							},
							REST: true,
							type: `POST`,
							url: `/api/users/${userId}/setup-intent`,
						});

					if (options.userId) {
						return create(options.userId);
					} else if (options.fanId) {
						return create(options.fanId);
					} else {
						return this.User.get({
							then: (user) => create(user.id),
						});
					}
				},
			},

			/**
			 * @method Payment#confirmIntent
			 * @param options.eventId - id of the event the purchase is being made for
			 * @param options.element - DOM element bound to the Stripe api
			 * @param options.token - tps token for the potential order
			 * @param billingAddress - billingAddress
			 * @param savePaymentMethod - values.savePaymentMethod
			 */
			confirmIntent: {
				async: true,
				public: true,
				defaults: {
					type: `payment`, // payment | setup
					paymentIntent: null, // can be overridden with custom account id
					userId: null,
				},
				value(context, options) {
					const { ShoppingCart } = this;
					const { paymentIntent, type, userId } = options;

					if (!this.isString(paymentIntent.clientSecret)) {
						throw new Error(`Payment.confirmIntent(): clientSecret required`);
					}

					// attempting payment, clear persistent checkout
					ShoppingCart.clearPersistentCheckout();

					if (type === `setup` && this.isString(paymentIntent.accountId, 1)) {
						this.log({
							type: `warn`,
							message: `Payment.confirmIntent(): account should never be provided for setupIntent`,
						});

						paymentIntent.accountId = null;
					}

					return this.useStripe({
						stripeAccount: paymentIntent.accountId, // pass through
						then: (stripe) => {
							const intentKey = type[0].toUpperCase() + type.slice(1); // Payment | Setup
							const stripeAuthorize = stripe[`confirmCard${intentKey}`];
							const params = {};
							const onError = (reason) => {
								if (this.isObject(reason)) {
									const { code } = reason;

									if (code === `lock_timeout`) {
										const then = makeStripeCall;
										duration *= 2; // use back-off

										if (duration > maxDuration) {
											duration = maxDuration;
										}

										return this.defer({ duration, then });
									}

									reason.docType = `STRIPE_ERROR`; // tag as a Stripe error
								}

								// payment failed, restore persistent if applicable
								ShoppingCart.setPersistentCheckout();

								throw reason;
							};
							const makeStripeCall = () => {
								let promise;
								// cvc token recollection has to be verified on server with secret key
								if (options.cvcToken) {
									promise = this.XHR.ajax({
										data: params,
										REST: true,
										type: `POST`,
										url: `/api/users/${userId}/confirm-intent`,
									});
								} else {
									promise = stripeAuthorize(paymentIntent.clientSecret, params);
								}

								return this.when({
									promise,
									then: (result) => {
										const { error } = result;

										if (!this.isUndefined(error)) {
											return onError(error);
										}

										if (result.requires3DSecure) {
											window.open(result.redirectUrl, `_blank`);
											return;
										}

										return result[`${type}Intent`];
									},
									fail: onError,
								});
							};
							const maxDuration = 10000;
							let duration = 250;

							if (options.cvcToken) {
								params.paymentMethod = options.payment_method;
								params.paymentIntent = options.paymentIntent;
								params.paymentMethodOptions = { card: { cvcToken: options.cvcToken } };
							} else {
								params.payment_method = options.payment_method;
							}

							return makeStripeCall();
						},
					});
				},
			},

			/**
			 * @method Payment#confirmPaymentIntent
			 * @param options.change - callback for when the form changes
			 * @param options.container - DOM element to bind the form to
			 * @param options.defaultPaymentMethod - default payment method to select
			 * @param options.paymentMethods - array of payment methods
			 * @param options.theme - theme for the form
			 * @param options.toggleSubmit - whether to toggle the submit button
			 * @param options.transition - transition for the form
			 * @param options.submit - callback for when the form is submitted
			 */
			createSavedPaymentForm: {
				public: true,
				async: true,
				async value(context, options) {
					const { instances, DOM } = this;
					this.defaults(options, {
						change: null,
						checkoutMode: null,
						container: null,
						defaultPaymentMethod: null,
						paymentMethods: [],
						theme: `LIGHT`,
						toggleSubmit: true,
						transition: `none`,
						submit: null,
						view: null,
					});

					const {
						change,
						checkoutMode,
						container,
						defaultPaymentMethod,
						event,
						isAgentMode,
						paymentIntent,
						paymentMethods,
						theme,
						toggleSubmit,
						transactionMetadataConfiguration,
						transactionMetadataElement,
						transition,
						submit,
						view,
					} = options;

					const termsContentElement = DOM.get(`[name="terms-content"]`, container);
					const termsContent = this.getTermsLabel({
						event,
						type: `expressCheckout`,
					});
					termsContentElement.innerHTML = termsContent;
					// Todo - use Information Message component

					const inputs = {
						paymentToken: {
							type: `PaymentMethodSelector`,
							rules: {
								options: paymentMethods,
							},
							validation: {
								required: true,
							},
							value: defaultPaymentMethod,
						},
						trueValue: {
							type: `Hidden`,
							validation: { type: `Boolean` },
							value: true,
						},
					};

					if (this.isArray(transactionMetadataConfiguration, 1)) {
						const { newElements, newInputs } = this.getTransactionMetadataSetup({
							isAgentMode,
							transactionMetadataConfiguration,
						});

						if (this.isArray(newElements, 1)) {
							this.each(newElements, (el) => {
								transactionMetadataElement.appendChild(el);
							});
							this.assign(inputs, { ...newInputs });
							DOM.show({ target: transactionMetadataElement });
						} else {
							DOM.hide({ target: transactionMetadataElement });
						}
					} else {
						DOM.hide({ target: transactionMetadataElement });
					}

					const form = await this.create({
						namespace: `Widgets/Form`,
						setup: {
							container,
							theme,
							toggleSubmit,
							transition,
							inputs,
							callbacks: {
								change: (value, name, inputs) => {
									if (this.isFunction(change)) {
										change(value, name, inputs);
									}
								},
								submit: (values, inputs) => {
									if (this.isFunction(submit)) {
										submit(values, inputs);
									}
								},
							},
						},
					});

					if (checkoutMode === `AGENT`) {
						const name = `card-cvc`;

						instances.recollectCvcForm = await this.create({
							namespace: `Modules/RecollectCvcForm`,
							setup: {
								name,
								container: document.querySelector(`[name="${name}"]`),
								mode: `checkout`,
								clientSecret: paymentIntent?.clientSecret,
								stripeAccount: paymentIntent?.accountId,
								paymentProcessorType: event.paymentProcessorType,
								view,
							},
							then: (recollectCvcForm) => {
								return recollectCvcForm;
							},
						});
					}

					return { savedPaymentForm: form, recollectCvcForm: instances.recollectCvcForm };
				},
			},

			/**
			 * STRIPE ELEMENTS
			 */

			/**
			 * Confirm a payment elements with Stripe. This is only used with new payment methods.
			 * @param options.shippingAddress - shipping address to be passed as billing if no billing element exists
			 */
			confirmPaymentElements: {
				public: true,
				value(context, options = {}) {
					return new Promise((resolve, reject) => {
						const { data } = this;
						const { isWallet, paymentAddressElement, returnUrl } = data;
						const { billingElementsGroup: elements, stripe } = data;
						options.addressMode = `billing`;

						return elements.submit().then((result) => {
							if (result.error) {
								result.error.docType = `STRIPE_ERROR`;
								result.error.message = `An error has occurred. Please review the form and try again.`;
								reject(result.error);
								return;
							}

							const params = {
								elements,
								confirmParams: {
									return_url: returnUrl,
								},
								redirect: `if_required`,
							};

							if (
								!this.isObject(paymentAddressElement) &&
								(!isWallet || options.addressMatch) &&
								this.isObject(options.shippingAddress)
							) {
								params.confirmParams.payment_method_data = {
									billing_details: {
										address: this.convertAddressElementTixrToStripe(options.shippingAddress).address,
										name: `${options.shippingAddress.firstname} ${options.shippingAddress.lastname}`,
									},
								};
							}

							return stripe.confirmPayment(params).then(function (response) {
								if (response.error) {
									response.error.docType = `STRIPE_ERROR`;
									reject(response.error);
								}

								resolve(response);
							});
						});
					});
				},
			},

			/**
			 * Clear all Stripe elements and related data
			 */
			clearStripeElementsInfo: {
				public: true,
				value(options) {
					const { data } = this;

					this.assign(data, {
						billingElementsGroup: null,
						expressCheckoutElement: null,
						paymentElement: null,
						paymentAddressElement: null,
						cvcElementsGroup: null,
						cvcElement: null,
						stripe: null,
						returnUrl: null,
					});
				},
			},

			/**
			 * Confirm a setup intent with Stripe. This is only used for waitlist with new payment methods.
			 * Note: This is using the existing stripe object instead of calling useStripe again. This is because Apple throws an error if there's too much time between the click event and submisstion call.
			 */
			confirmSetupIntent: {
				async: true,
				public: true,
				value(context, options = {}) {
					const { data } = this;
					const { billingElementsGroup: elements, isWallet, paymentAddressElement, returnUrl, stripe } = data;
					const { addressMatch, redirectUrl, shippingAddress } = options;

					return new Promise((resolve, reject) => {
						return elements.submit().then((result) => {
							if (result.error) {
								result.error.docType = `STRIPE_ERROR`;
								reject(result.error);
								return;
							}

							const params = {
								elements,
								confirmParams: {
									return_url: this.isString(redirectUrl, 1) ? redirectUrl : returnUrl,
								},
								redirect: `if_required`,
							};

							if (
								!this.isObject(paymentAddressElement) &&
								(!isWallet || addressMatch) &&
								this.isObject(shippingAddress)
							) {
								params.confirmParams.payment_method_data = {
									billing_details: {
										address: this.convertAddressElementTixrToStripe(shippingAddress).address,
										name: `${shippingAddress.firstname} ${shippingAddress.lastname}`,
									},
								};
							}

							return stripe.confirmSetup(params).then(function (result) {
								if (result.error) {
									reject(result.error);
								}

								resolve(result.setupIntent);
							});
						});
					});
				},
			},

			/**
			 * Set the return url for stripe to redirect to after payment
			 */
			setRedirectUrl: {
				public: true,
				value(context, options) {
					this.data.returnUrl = options.returnUrl;
				},
			},

			/**
			 * Get the appearance for the stripe elements
			 * This is also called on window resize to update the appearance of the stripe elements
			 */
			getStripeElementAppearance: {
				public: true,
				value(context) {
					const currentFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
					const regularFontSize = 1;
					const regularLineHeight = 1.43;

					return {
						theme: `flat`,
						labels: `floating`,
						variables: {
							colorPrimary: `#1fa3ff`, // Sets tab color and border for focused elements
							colorBackground: `#FFFFFF`,
							colorText: `#111111`,
							colorDanger: `#df1b41`,
							fontFamily: `Greycliff CF, system-ui, sans-serif`,
							spacingUnit: `${0.29 * currentFontSize}px`,
							borderRadius: `8px`,
							colorTextPlaceholder: `#888888`,
							gridRowSpacing: `${0.57 * currentFontSize}px`,
						},
						rules: {
							'.Label--resting': {
								fontSize: `${regularFontSize * currentFontSize}px`,
								fontWeight: `500`,
								lineHeight: `${regularLineHeight * currentFontSize}px`,
							},

							'.Label--floating': {
								fontSize: `${0.86 * currentFontSize}px`,
								fontWeight: `500`,
								opacity: `0.7`,
								lineHeight: `${1.14 * currentFontSize}px`,
							},

							'.Input': {
								fontSize: `${regularFontSize * currentFontSize}px`,
								fontWeight: `500`,
								lineHeight: `${regularLineHeight * currentFontSize}px`,
								// fontLineHeight: `${regularLineHeight * currentFontSize}px`,
								padding: `${0.86 * currentFontSize}px ${1.14 * currentFontSize}px`,
								border: `1px solid #dcdee4`,
							},
							'.TabLabel': {
								fontSize: `${1 * currentFontSize}px`,
								fontWeight: `500`,
							},
						},
					};
				},
			},

			/**
			 * terms and conditions label for checkout
			 */
			getTermsLabel: {
				public: true,
				value(_, options) {
					this.defaults(options, {
						type: `submit`, // submit | expressCheckout
					});
					const { event, type } = options;
					const labelParts = [];

					if (type === `submit`) {
						labelParts.push(
							`By completing your purchase, you agree to Tixr's <a href="#" action="load-terms-modal">Terms of Service</a> and <a href="#" action="load-privacy-modal">Privacy Policy</a>`
						);
					} else {
						// expressCheckout
						labelParts.push(
							`By continuing with any payment method below, you agree to Tixr's <a href="#" action="load-terms-modal">Terms of Service</a> and <a href="#" action="load-privacy-modal">Privacy Policy</a>`
						);
					}

					const legalLinks = [];
					if (event.ll?.tacu) {
						legalLinks.push(`<a href="${event.ll.tacu}" target="_blank">Terms</a>`);
					}
					if (event.ll?.ppu) {
						legalLinks.push(`<a href="${event.ll.ppu}" target="_blank">Privacy Policy</a>`);
					}
					if (legalLinks.length !== 0) {
						labelParts.push(`, as well as ${event.group?.name}'s `);
						labelParts.push(legalLinks.join(` and `));
					}
					labelParts.push(`.`);
					return labelParts.join(``);
				},
			},

			/**
			 * Create the stripe elements for the payment form
			 * @param options.clientSecret - client secret for the payment intent
			 * @param options.stripeAccount - stripe account id
			 * @param options.address - shipping address
			 * @param options.checkoutConfig - object including paymentMethods, paymentWallets, paymentCardBrands
			 * @param options.walletOverrides - object including applePay and googlePay (true or false)
			 */
			createBillingElements: {
				public: true,
				async: true,
				async value(context, options) {
					const { data, User } = this;
					const { billingElementsGroup } = data;
					const { clientSecret, stripeAccount, walletOverrides } = options;
					let { checkoutConfig } = options;
					let elements;
					const { allowedCardBrands, showLink } = this.getCheckoutConfigBreakdown({
						checkoutConfig,
						walletOverrides,
					});

					options.showLink = showLink; // pass through to the address element
					options.addressMode = `billing`;
					if (!this.isObject(checkoutConfig)) {
						checkoutConfig = {
							paymentMethods: null,
							paymentWallets: null,
							paymentCardBrands: null,
						};
					}

					if (this.isObject(billingElementsGroup)) {
						elements = billingElementsGroup;
					} else {
						const stripe = await this.useStripe({ stripeAccount });
						data.stripe = stripe; // TODO: can this be removed if we rewrite promises.stripe
						elements = await stripe.elements({
							clientSecret,
							allowedCardBrands,
							appearance: this.getStripeElementAppearance(),
						});
					}

					data.billingElementsGroup = elements;

					const existingPaymentElement = data.paymentElement;

					if (!this.isObject(existingPaymentElement) || existingPaymentElement?._destroyed) {
						const paymentOptions = {
							layout: `tabs`,
							fields: {
								billingDetails: {
									address: `never`,
								},
							},
							wallets: {
								applePay: `never`, // expressCheckout will handle this
								googlePay: `never`, // expressCheckout will handle this
								link: showLink ? `auto` : `never`,
							},
							terms: {
								applePay: `never`,
								auBecsDebit: `never`,
								bancontact: `never`,
								card: `never`,
								cashapp: `never`,
								googlePay: `never`,
								ideal: `never`,
								paypal: `never`,
								sepaDebit: `never`,
								sofort: `never`,
								usBankAccount: `never`,
							},
						};

						if (showLink) {
							try {
								const user = await User.get();
								const { email, firstName, lastName } = user;
								let name = this.isString(firstName, 1) ? firstName : ``;
								name += this.isString(lastName, 1) ? ` ${lastName}` : ``;
								name = name.trim();

								paymentOptions.defaultValues = {
									billingDetails: {
										email,
										name,
									},
								};
							} catch (error) {
								this.log({
									type: `error`,
									message: `User data of email and name was not sent to Stripe for Link integration when creating payment element`,
								});
							}
						}

						const paymentElement = elements.create(`payment`, paymentOptions);
						data.paymentElement = paymentElement;
					}

					await this.getBillingAddressElement(options);

					return {
						elementsGroup: elements,
						paymentElement: data.paymentElement,
						addressElement: data.paymentAddressElement,
					};
				},
			},

			createExpressCheckoutElements: {
				public: true,
				async value(context, options) {
					const { confirmPaymentExpressCheckout, data, Config } = this;
					const { expressCheckoutElement: existingExpressCheckoutElement } = data;
					const {
						clientSecret,
						forceNewElement,
						groupName,
						onLoadError,
						onReady,
						return_url,
						stripeAccount,
						walletOverrides,
					} = options;
					const { ALL_COUNTRIES } = Config;
					let { checkoutConfig } = options;
					let elements;
					const { allowedCardBrands, showApplePay, showGooglePay, showLink } = this.getCheckoutConfigBreakdown({
						checkoutConfig,
						walletOverrides,
					});

					if (!this.isObject(checkoutConfig)) {
						checkoutConfig = {
							paymentMethods: null,
							paymentWallets: null,
							paymentCardBrands: null,
						};
					}

					if (!this.isString(groupName, 1)) {
						throw new Error(`Payment.createExpressCheckoutElements(): groupName is required`);
					}

					const stripe = await this.useStripe({ stripeAccount });
					data.stripe = stripe; // TODO: can this be removed if we rewrite promises.stripe
					// Create new elements group. If existing, it may be a setup intent from new payment method. Needs to be a payment intent for express checkout
					elements = await stripe.elements({
						clientSecret,
						allowedCardBrands,
						appearance: this.getStripeElementAppearance(),
					});

					data.billingElementsGroup = elements;

					if (
						!this.isObject(existingExpressCheckoutElement) ||
						existingExpressCheckoutElement?._destroyed ||
						forceNewElement
					) {
						const currentFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
						const minHeightAllowed = 40; // 40 is the min pixel height allowed by Stripe
						const maxHeightAllowed = 55; // 55 is the max pixel height allowed by Stripe
						const buttonHeight = Math.max(Math.min(4 * currentFontSize, maxHeightAllowed), minHeightAllowed); // 4 is the figma height in rems

						const expressCheckoutOptions = {
							allowedShippingCountries: ALL_COUNTRIES,
							business: {
								name: `${groupName} via Tixr`, // We need this so Apple doesn't reject the app
							},
							buttonHeight: buttonHeight,
							buttonTheme: {
								applePay: `black`,
								googlePay: `black`,
							},
							buttonType: {
								applePay: `plain`,
								googlePay: `plain`,
							},
							layout: {
								maxColumns: 1,
								maxRows: 1,
							},
							overflow: `auto`,
							paymentMethods: {
								amazonPay: `never`,
								applePay: showApplePay ? `auto` : `never`,
								googlePay: showGooglePay ? `auto` : `never`,
								link: showLink ? `auto` : `never`,
								paypal: `never`,
								klarna: `auto`, //`never`,
							},
							paymentMethodOrder: [`applePay`, `googlePay`, `link`, `klarna`],
						};

						const expressCheckoutElement = elements.create(`expressCheckout`, expressCheckoutOptions);
						data.expressCheckoutElement = expressCheckoutElement;
					}

					// update handlers so they're applied to the correct dom elements
					data.expressCheckoutElement.on(`confirm`, function (event) {
						confirmPaymentExpressCheckout({ clientSecret, return_url });
					});

					data.expressCheckoutElement.on(`ready`, ({ availablePaymentMethods }) => {
						onReady({ availablePaymentMethods });
					});
					data.expressCheckoutElement.on(`loaderror`, (event) => {
						onLoadError(event);
					});

					return {
						elementsGroup: elements,
						expressCheckoutElement: data.expressCheckoutElement,
					};
				},
			},

			confirmPaymentExpressCheckout: {
				public: true,
				value(context, options = {}) {
					const { data, App } = this;
					const { return_url } = options;
					const { billingElementsGroup: elements, stripe } = data;
					const { clientSecret } = options;

					stripe
						.confirmPayment({
							elements,
							clientSecret,
							confirmParams: {
								return_url,
							},
							redirect: `always`,
						})
						.then(function (result) {
							if (result.error) {
								// Inform the customer that there's an error.
								result.error.docType = `STRIPE_ERROR`;
								App.notifyUser({
									type: `error`,
									message: result.error.message || `An error has occurred. Please try again later.`,
								});
								return;
							}
						});
				},
			},

			/**
			 * Get the billing address element for the payment form
			 * @param options.address - shipping address
			 */
			getBillingAddressElement: {
				public: true,
				value(context, options) {
					const { data, Config } = this;
					const { ALL_COUNTRIES } = Config;
					const { billingElementsGroup: elements } = data;
					const { address, prefersEnGb, showLink } = options;
					let defaultAddressValues = {};

					const existingPaymentAddressElement = data.paymentAddressElement;

					if (!this.isObject(existingPaymentAddressElement) || existingPaymentAddressElement?._destroyed) {
						if (!showLink) {
							// Link shouldn't default to a country so it can be auto-filled by stripe
							if (this.isNull(address)) {
								defaultAddressValues.address = {
									country: prefersEnGb ? `GB` : `US`,
								};
							} else {
								// Don't need to add entire address because there's a checkbox for "Use the same address as shipping"
								defaultAddressValues.address = {
									country: address.countryCodeAlpha2,
								};
							}
						}

						const addressOptions = {
							mode: `billing`,
							allowedCountries: ALL_COUNTRIES,
							defaultValues: defaultAddressValues,
							display: {
								name: `split`,
							},
						};

						const addressElement = elements.create(`address`, addressOptions);
						data.paymentAddressElement = addressElement;

						return addressElement;
					}
				},
			},

			getBillingAddressElementBraintree: {
				public: true,
				async: true,
				async value(context, options) {
					const { data } = this;
					const { address, prefersEnGb } = options;
					let defaultAddressValues = {};

					const existingPaymentAddressElement = data.paymentAddressElement;
					const billingElementsGroup = data.billingElementsGroup;

					if (!this.isObject(existingPaymentAddressElement)) {
						if (this.isNull(address)) {
							defaultAddressValues.address = {
								country: prefersEnGb ? `GB` : `US`,
							};
						} else {
							defaultAddressValues = address;
						}

						const addressOptions = {
							autocomplete: {
								mode: `google_maps_api`,
								apiKey: window.SiteConfig.GOOGLE_MAPS_JS_API_KEY,
							},
							defaultValues: defaultAddressValues,
							display: {
								name: `split`,
							},
							mode: `shipping`, // Need to create as a shipping elmeent instead since no stripe info exists
						};

						const stripe = await this.useStripe({});
						const elements = await stripe.elements({ appearance: this.getStripeElementAppearance() });
						const addressElement = elements.create(`address`, addressOptions);
						data.billingElementsGroup = elements;
						data.paymentAddressElement = addressElement;

						return { elementsGroup: elements, addressElement };
					} else {
						return { elementsGroup: billingElementsGroup, addressElement: existingPaymentAddressElement };
					}
				},
			},

			/**
			 * Destroy the billing address element
			 * Note: This is called when "use same address as shipping" is selected.
			 */
			destroyBillingAddressElement: {
				public: true,
				value(context, options = {}) {
					const { data } = this;
					const { paymentAddressElement, paymentElement } = data;

					if (this.isObject(paymentAddressElement)) {
						paymentAddressElement.destroy();
						data.paymentAddressElement = null;
					}

					// Add billing address details to the payment element
					if (this.isObject(paymentElement) && this.isObject(options.address)) {
						const stripeAddressDetails = this.convertAddressElementTixrToStripe(options.address);
						paymentElement.update({
							defaultValues: {
								billingDetails: stripeAddressDetails,
							},
						});
					}
				},
			},

			/**
			 * Create the shipping address element for the shipping form
			 * @param options.defaultValues - default values for the address
			 * @param options.allowedCountries - allowed countries for the address
			 * @param options.prefersEnGb - whether the user prefers en-gb
			 * @param options.stripeAccount - stripe account id
			 */
			createShippingAddressElement: {
				public: true,
				async: true,
				async value(context, options) {
					const { data } = this;
					const { defaultValues, allowedCountries, prefersEnGb } = options;
					const { address } = defaultValues;
					let defaultAddressValues = {};

					if (this.isNull(address)) {
						defaultAddressValues.address = {
							country: prefersEnGb ? `GB` : `US`,
						};
					} else {
						defaultAddressValues = defaultValues;
					}

					const addressOptions = {
						mode: `shipping`,
						allowedCountries: allowedCountries,
						autocomplete: {
							mode: `google_maps_api`,
							apiKey: window.SiteConfig.GOOGLE_MAPS_JS_API_KEY,
						},
						defaultValues: defaultAddressValues,
						display: {
							name: `split`,
						},
					};

					const stripe = await this.useStripe({});
					const elements = await stripe.elements({ appearance: this.getStripeElementAppearance() });
					const addressElement = elements.create(`address`, addressOptions);

					data.addressElementsGroup = elements;

					return { elementsGroup: elements, addressElement }; // still needs to be mounted by caller of the function -> addressElement.mount(`#address-element`);
				},
			},

			/**
			 * Create the cardCvc element for agents to use a fan's saved payment methods
			 * @param options.clientSecret - client secret for the payment intent
			 * @param options.stripeAccount - stripe account id
			 */
			createCardCvcElement: {
				public: true,
				async: true,
				async value(context, options) {
					const { data } = this;
					const { cvcElementsGroup, cvcElement } = data;
					const { clientSecret, stripeAccount } = options;
					let elements;
					let cardCvcElement;

					if (this.isObject(cvcElementsGroup)) {
						elements = cvcElementsGroup;
						cardCvcElement = cvcElement;
					} else {
						const stripe = await this.useStripe({ stripeAccount });
						// can't be styled via the same method as the stripe element sets
						elements = await stripe.elements({ clientSecret });
						cardCvcElement = elements.create(`cardCvc`, { placeholder: `` });
					}

					data.cvcElementsGroup = elements;
					data.cvcElement = cardCvcElement;

					return { elementsGroup: elements, cardCvcElement }; // still needs to be mounted by caller of the function -> cardCvcElement.mount(`#card-cvc-element`);
				},
			},

			/**
			 * Notify Payment if a wallet is being used for stripe
			 */
			toggleIsWallet: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { paymentElement } = data;
					const { isWallet } = options;

					data.isWallet = isWallet;
					paymentElement.update({
						fields: {
							billingDetails: {
								address: isWallet ? `auto` : `never`,
							},
						},
					});
				},
			},

			/**
			 * Convert the address element from Tixr to Stripe
			 */
			convertAddressElementTixrToStripe: {
				public: true,
				value(context, values) {
					const { city, countryCodeAlpha2, extendedAddress, firstname, lastname, postalCode, state, streetAddress } =
						values;

					return {
						firstName: firstname,
						lastName: lastname,
						address: {
							line1: streetAddress,
							line2: extendedAddress,
							city,
							state,
							postal_code: postalCode,
							country: countryCodeAlpha2,
						},
					};
				},
			},

			/**
			 * Convert the address element from Stripe to Tixr
			 */
			convertAddressElementStripeToTixr: {
				public: true,
				value(context, values) {
					const { address, firstName, lastName } = values;
					const { city, country, line1, line2, postal_code, state } = address;

					return {
						city,
						countryCodeAlpha2: country,
						extendedAddress: line2,
						firstname: firstName,
						lastname: lastName,
						postalCode: postal_code,
						state,
						streetAddress: line1,
					};
				},
			},

			getCheckoutConfigBreakdown: {
				public: true,
				value(context, options) {
					let { checkoutConfig, walletOverrides } = options;
					if (!this.isObject(checkoutConfig)) {
						checkoutConfig = {
							paymentMethods: null,
							paymentWallets: null,
							paymentCardBrands: null,
						};
					}

					const { paymentCardBrands, paymentMethods, paymentWallets } = checkoutConfig;
					const allowedCardBrands = this.isArray(paymentCardBrands, 1) ? paymentCardBrands : undefined;
					let showApplePay = !this.isArray(paymentWallets) || this.includes(paymentWallets, `applePay`);
					let showGooglePay = !this.isArray(paymentWallets) || this.includes(paymentWallets, `googlePay`);
					let showLink = !this.isArray(paymentWallets) || this.includes(paymentWallets, `link`);
					let showNonCardOptions = showApplePay || showGooglePay;
					let allowCards = this.includes(paymentMethods, `card`) || paymentMethods == null;

					if (this.isObject(walletOverrides)) {
						if (this.isBoolean(walletOverrides.applePay)) {
							showApplePay = walletOverrides.applePay;
						}
						if (this.isBoolean(walletOverrides.googlePay)) {
							showGooglePay = walletOverrides.googlePay;
						}
					}

					return { allowCards, allowedCardBrands, showApplePay, showGooglePay, showLink, showNonCardOptions };
				},
			},

			getTransactionMetadataSetup: {
				public: true,
				value(context, options) {
					const { isAgentMode, transactionMetadataConfiguration } = options;
					const newInputs = {};
					const newElements = [];

					this.each(transactionMetadataConfiguration, (setup, index) => {
						if (!isAgentMode && !setup.fanFacing) {
							return; // skip non-fan facing options in non-agent mode
						}

						const name = `transaction-metadata-${setup.id}`;
						const metadataElement = document.createElement(`div`);
						const options = this.map(setup.options, (option) => ({
							label: option.label,
							value: option.id,
							ordinal: option.ordinal,
						}));
						options.sort((a, b) => a.ordinal - b.ordinal); // sort alphabetically by label

						metadataElement.setAttribute(`name`, name);

						newElements.push(metadataElement);
						newInputs[name] = {
							type: `Dropdown`,
							// label: mappedSetup.label,
							ordinal: index,
							rules: {
								label: setup.label,
								options,
							},
							validation: {
								type: `Number`,
								required: setup.required,
							},
						};
					});

					return { newElements, newInputs };
				},
			},
		},
	});
});

//# sourceMappingURL=Payment.js.map